/**
 * Master-solution REFINE — rewriting a solution that already exists, from teacher feedback.
 *
 * The counterpart of `gs-refine.job.ts`, and a job for the same reasons: everything said there about why a
 * refine is a third producer (generation declines a task that already has a solution, extraction reads an
 * upload) and why it is a PARTIAL run applies here unchanged.
 *
 * It exists as a job even though its only trigger today is synchronous — `refine_master_solution`, which the
 * teacher waits on. `dispatchRefineAndWait` keeps that wait, so the tool behaves as it did while the work
 * itself gains a lease, retries, the LLM gate and cancellation; a restart mid-refine resumes instead of
 * losing it. And it means a future trigger that ISN'T a teacher waiting — a bulk correction, a re-upload
 * chain — has a job to enqueue rather than an agent call to smuggle into a hook.
 *
 * It dispatches nothing. Once no `masterSolution` job is left live for the exam, that step's completion reads what
 * the refine actually changed and decides whether a grading-scheme refine should follow — see
 * `steps/master-solution.step.ts`.
 */

import { logger } from '@adornis/base/logging.js';
import type { RefineRequest } from '../refine.js';
import { refineMasterSolutionAgent } from '../../agents/ms-generation-agent.js';
import { masterSolutionStep } from '../steps/master-solution.step.js';
import * as conditions from '../job-conditions.js';
import { taskTreeLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface MsRefinePayload {
  /** Main task numbers whose solution is being rewritten. */
  taskRefs: string[];
  /** The teacher's feedback, handed to the model. */
  feedback: string;
  [key: string]: unknown;
}

export const masterSolutionRefineJob = defineJob<MsRefinePayload>({
  type: 'msRefine',

  /**
   * `reupload: false` — a refine is a correction to this artifact, not a fresh delivery of it, and the announcement
   * words the two differently. The per-pass fields are NOT cleared from here — `startSolutionProgress` does that, in
   * the write that declares the record IN_PROGRESS, which for a refine is inside the agent. Clearing them at dispatch
   * wiped the sets of whatever pass was still running on this shared record.
   *
   * NO `open`: it deliberately writes neither the status nor the done set, and the two restrictions have the same
   * cause. `producer` is what `artifactExtractionRunning` reads to decide whether a refine may run beside an upload, so
   * writing it here would overwrite the very state this row's own gate is about to consult — and a status write without
   * it is worse rather than safer: on an artifact whose content came from an upload the producer is still
   * `extraction`, so setting IN_PROGRESS at dispatch would make the refine's gate see an extraction in flight and defer
   * the row indefinitely. Which leaves the done set, and it has to move WITH the status or not at all: pulling the
   * units here while the record still said COMPLETED is what displayed a finished artifact at 0 of 4, a bar that had
   * gone backwards with nothing to explain it, minutes before the job that would say IN_PROGRESS was claimed. The
   * refine agents do both together (`dropSolutionTasks` then `startSolutionProgress`), which is the only place that
   * knows the work has actually begun.
   */
  onStepStart: { reupload: false },

  /**
   * ONE ROW carrying every ref, not one row per ref: the refine agents take the set and reason across it, so there is
   * no per-task split to make. No refs named means nothing to refine, and returning no units is how that is said —
   * which also means no run is opened for a refine that had nothing to do.
   *
   * `joinTwin`, because a tool the teacher is waiting on has to report on whichever row is doing the work.
   */
  batch: {
    units: (_examId, scope) => {
      const { taskRefs = [], feedback = '' } = scope as Partial<RefineRequest>;
      return taskRefs.length ? [{ taskRefs, feedback }] : [];
    },
    joinTwin: true,
    /** Generous, because the caller is a teacher who asked for this specific change and wants to hear that it landed. */
    waitMs: 20 * 60_000,
  },

  /** Same step as the extraction and the generation, so a refine mid-extraction holds the step open. */
  onStepComplete: masterSolutionStep,

  /**
   * The master-solution artifact key, not a per-task one: like a grading-scheme refine, this is ONE job over
   * a set of refs because the agent reasons across them, and sharing the artifact key is what keeps it from
   * running beside an extraction of the same field.
   */
  lockKey: examId => `${taskTreeLockKey(examId)}:masterSolution`,

  dedupeKey: (examId, payload) => `msRefine:${examId}:${[...payload.taskRefs].sort().join(',')}`,

  ready: (exam, job) => {
    if (!conditions.hasTasks(exam)) return { verdict: 'wait', because: 'msRefine:no-tasks' };
    if (job.payload.taskRefs.length === 0) return { verdict: 'drop', because: 'msRefine:no-tasks-named' };
    // A teacher uploading a master solution supersedes refining the old one.
    if (conditions.artifactExtractionRunning(exam, 'masterSolution')) {
      return { verdict: 'wait', because: 'msRefine:extraction-running' };
    }
    return 'run';
  },

  maxRuntimeMs: 15 * 60_000,
  maxAttempts: 2,

  handler: async (examId, payload) => {
    logger.info({ examId, taskRefs: payload.taskRefs }, 'Refining master solution');
    const result = await refineMasterSolutionAgent(examId, payload.taskRefs, payload.feedback);
    if (!result.success) throw new Error(result.error ?? 'Master solution refine reported a failure');
    return { note: `refined master solution for ${payload.taskRefs.join(', ')}` };
  },
});
